import { useMemo, useState } from 'react';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { OPTIONAL_EXTRA_PRODUCT_TYPE_MAPPING } from 'utils/constant';
import { price, processPassengerType, toSentence } from 'utils/helpers';
import {
  FORMAT_FULL_DATE_MINI,
  FORMAT_WEEKDAY_DATE_YEAR,
  formatDate,
} from 'utils/helpers/datetime';
import defaultThumbnail from 'images/ccc-checkout-default-thumbnail.jpg';

export function BookingCartItem({
  reservation,
  passengerTypes,
  booking,
  onEdit,
  onRemove,
}) {
  const {
    id,
    booking_id,
    image,
    selection_name,
    first_travel_date,
    last_travel_date,
    passenger_splits,
    travel_offset_days,
    sub_reservations_attributes = [],
    gross_including_packaged_item_in_cents,
    pre_adjusted_gross_including_packaged_item_in_cents,
    rules,
    resource_class_name,
    product_name_underscore,
    selection_display_name,
  } = reservation;
  const passengerDetail = processPassengerType(
    passenger_splits,
    booking.passengers_attributes,
    passengerTypes,
    resource_class_name === 'Package' ? sub_reservations_attributes : null
  );
  const [removalStatus, setRemovalStatus] = useState(
    booking.paid_in_cents > 0 ? null : 'remove'
  );
  const customerFacingAdjustments = useMemo(() => {
    const groups = {};
    (reservation.adjustments_attributes || [])
      .filter((adj) => adj.display_to_customer)
      .forEach((adj) => {
        const key = adj.adjustment_category_id;
        if (!groups[key]) {
          groups[key] = {
            category_id: key,
            display_name: adj.display_name,
            total: 0,
          };
        }
        groups[key].total += adj.gross_in_cents;
      });
    return Object.values(groups);
  }, [reservation.adjustments_attributes]);

  // The term add-on is interchangeable with optional extra
  const validAddons = useMemo(() => {
    const isPackage = resource_class_name === 'Package';
    const optionalExtras = sub_reservations_attributes.filter(
      (extra) =>
        extra.product_type_id ===
        OPTIONAL_EXTRA_PRODUCT_TYPE_MAPPING[
          ecom.craftConfig?.site || ecom.craftConfig?.tenant
        ]
    );

    if (!isPackage) {
      return sub_reservations_attributes;
    }

    // For package reservations, we only show optional extras in the Optional Extras section
    if (optionalExtras.length) {
      return optionalExtras;
    }

    return [];
  }, [resource_class_name, sub_reservations_attributes]);

  const renderPrice = () => {
    const discountInCents =
      pre_adjusted_gross_including_packaged_item_in_cents -
      gross_including_packaged_item_in_cents;

    if (
      gross_including_packaged_item_in_cents === undefined ||
      pre_adjusted_gross_including_packaged_item_in_cents === undefined ||
      discountInCents <= 0
    ) {
      return (
        <div className="booking-cart-item__price">
          <div>
            <span className="font-bold text-large">
              {price(gross_including_packaged_item_in_cents)}
            </span>
          </div>
        </div>
      );
    }

    const rulesApplied = rules?.map((r) => r.display_name) || [];

    return (
      <div className="booking-cart-item__price">
        <div>
          <span className="text-small text-body-secondary text-line-through">
            {price(pre_adjusted_gross_including_packaged_item_in_cents)}
          </span>{' '}
          <span className="font-bold text-large">
            {price(gross_including_packaged_item_in_cents)}
          </span>
        </div>
        <div className="text-x-small text-success">
          You saved {price(discountInCents)}
          {rulesApplied.length >= 1 &&
            ` with ${pluralize('special', rulesApplied.length)}: ${toSentence(
              rulesApplied
            )}`}
        </div>
      </div>
    );
  };

  const isGiftVoucher =
    resource_class_name === 'Package' &&
    product_name_underscore === 'gift_vouchers';

  return (
    <div className="booking-cart-item border">
      <div className="booking-cart-item__image-wrapper p-3 d-none d-sm-block">
        <img
          className="img-fluid"
          src={image || defaultThumbnail}
          alt={selection_display_name || selection_name}
        />
      </div>
      <div className="booking-cart-item__content-wrapper flex-grow-1 space-y-2">
        <div className="booking-cart-item__top">
          <div className="booking-cart-item__name">
            {selection_display_name || selection_name}
          </div>
          <div className="booking-cart-item__actions pt-2">
            {/* Modifier classes are purly for testing purpose */}
            {/* TODO: Use `@testing-library/react's test-ids */}
            <span
              className="booking-cart-item__action booking-cart-item__action--edit d-none"
              onClick={() => onEdit(id)}
            >
              Edit
            </span>
            {removalStatus === 'removing' && (
              <span className="booking-cart-item__action--disabled text-danger">
                <i className="fas fa-spinner-third animation-momentum-spin me-1" />
                Removing
              </span>
            )}
            {removalStatus === 'remove' && (
              <span
                className="booking-cart-item__action booking-cart-item__action--remove text-danger"
                onClick={() => {
                  setRemovalStatus('removing');
                  onRemove(booking_id, id)?.finally(() => {
                    setRemovalStatus('remove');
                  });
                }}
              >
                Remove
              </span>
            )}
          </div>
        </div>

        <div className="booking-cart-item__date">
          <i className="far fa-calendar-alt me-2" />
          {travel_offset_days
            ? `Valid until ${formatDate(
                last_travel_date._value,
                FORMAT_FULL_DATE_MINI
              )}`
            : formatDate(first_travel_date._value, FORMAT_WEEKDAY_DATE_YEAR)}
        </div>
        {!isGiftVoucher && (
          <div className="booking-cart-item__pax">
            {map(passengerDetail, (value, key) => (
              <div key={key}>
                {pluralize(key, value.count, true)}
                {value.price > 0 && <> × {price(value.price)}</>}
              </div>
            ))}
          </div>
        )}
        {validAddons.length > 0 && (
          <div className="booking-cart-item__add-ons">
            <div className="booking-cart-item__add-on-title">
              Optional Extras
            </div>
            {validAddons.map((subReservation) => (
              <div
                className="booking-cart-item__add-on"
                key={subReservation.id}
              >
                <div>
                  {subReservation.selection_display_name ||
                    subReservation.selection_name}
                  {subReservation.quantity && subReservation.quantity > 1 && (
                    <> x {subReservation.quantity}</>
                  )}
                </div>
                <div>{price(subReservation.pre_adjusted_gross_in_cents)}</div>
              </div>
            ))}
          </div>
        )}
        {customerFacingAdjustments.length > 0 && (
          <div className="booking-cart-item__add-ons">
            {customerFacingAdjustments.map((group) => (
              <div
                className="booking-cart-item__add-on"
                key={group.category_id}
              >
                <div>{group.display_name || 'Surcharge'}</div>
                <div>{price(group.total)}</div>
              </div>
            ))}
          </div>
        )}
        {renderPrice()}
      </div>
    </div>
  );
}

BookingCartItem.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  reservation: PropTypes.object,
  passengerTypes: PropTypes.object,
  booking: PropTypes.object,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,
};
